import classNames from 'classnames';
import { Plus } from 'lucide-react';
import { MediaManagerTemplate } from 'modules/core/domain/interfaces/supabase/media-manager/entity';
import MediaManagerTemplateListElement from 'modules/manager/ui/components/media-manager-template-list-element';
import { TemplateListSkeleton } from 'modules/manager/ui/components/skeletons';
import React from 'react';
import { AsyncState } from 'shared/utils/async-state';
import { UDButton, UDText } from 'ud-ui/components';
import ListView from 'ud-ui/components/list-view/list-view';

const HEADER_NAME = 'Шаблоны';

interface IProps {
  templates: MediaManagerTemplate[];
  /** Состояние выдачи, посчитанное `deriveAsyncState` (`.claude/docs/async-loading-states.md`). */
  state: AsyncState;
  className?: string;
  /** Row to flash after it was just created (e.g. duplicated from a preset). */
  highlightedTemplateId?: number | null;

  onCreate: () => void;
  onRetry: () => void;
  onSelect: (template: MediaManagerTemplate) => void;
  onEdit: (template: MediaManagerTemplate) => void;
  onDelete: (template: MediaManagerTemplate) => void;
  onExport: (template: MediaManagerTemplate) => void;
  isExporting?: boolean;
}

function MediaManagerTemplateList(props: IProps) {
  const { templates, state, className, highlightedTemplateId, isExporting, onCreate, onRetry, onSelect, onEdit, onDelete, onExport } =
    props;
  const nodeClasses = classNames(className, 'flex-1');

  if (state.status === 'loading') {
    return (
      <ListView header={HEADER_NAME} className={nodeClasses}>
        <TemplateListSkeleton />
      </ListView>
    );
  }

  if (state.status === 'error') {
    return (
      <ListView header={HEADER_NAME} className={nodeClasses}>
        <div className={'flex-center flex h-full flex-col gap-2 bg-white'} role="alert">
          <UDText variant={'body-2'}>Не удалось загрузить шаблоны</UDText>
          <UDButton variant="secondary" onClick={onRetry}>
            Повторить
          </UDButton>
        </div>
      </ListView>
    );
  }

  if (state.status === 'empty') {
    return (
      <ListView header={HEADER_NAME} className={nodeClasses}>
        <div className={'flex-center flex h-full flex-col gap-2 bg-white'}>
          <UDText variant={'body-2'}>У вас ещё нет шаблонов</UDText>
          <UDButton variant="primary" onClick={onCreate}>
            <Plus size={16} className={'mr-2'} /> Добавить шаблон
          </UDButton>
        </div>
      </ListView>
    );
  }

  return (
    <ListView header={HEADER_NAME} className={nodeClasses}>
      {/* Обновление после создания / удаления не прячет список: он приглушается, как у `UDAsyncContent`. */}
      <div
        className={classNames('h-full bg-white transition-opacity', { 'pointer-events-none opacity-50': state.isRefreshing })}
        aria-busy={state.isRefreshing}
        data-testid="template-list-rows"
      >
        {templates.map((template, index) => (
          <MediaManagerTemplateListElement
            key={template.id}
            id={template.id}
            name={template.name}
            background={index % 2 === 0 ? 'light' : 'dark'}
            isHighlighted={template.id === highlightedTemplateId}
            onClick={() => onSelect(template)}
            onEdit={() => onEdit(template)}
            onDelete={() => onDelete(template)}
            onExport={() => onExport(template)}
            isExporting={isExporting}
          />
        ))}
      </div>
    </ListView>
  );
}

export default MediaManagerTemplateList;
